// Portal: Site Status. The customer's fleet with xsiteio activity
// timestamps rendered as green/amber/grey health dots. Rows expand to
// a detail panel where the customer can edit their own site contact
// fields (phone / manager / manager email) -- server whitelists those
// three columns and logs every change as a CRM note.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Badge from '../../components/Badge.jsx';
import MODULE_BADGES from '../../lib/moduleBadges.js';

// Human labels for the module keys the API returns. Mirrors the
// internal Subscription Modules panel.
const MODULE_LABELS = {
  live_transactions: 'Real Time Inventory',
  price_book:        'Price Book',
  fuel_manager:      'Fuel Manager',
  charge:            'Charge Accounts',
  tx_exempt:         'Tax Exempt',
  car_wash:          'Car Wash',
  adv_report:        'Advanced Reporting',
  fuel_price:        'Fuel Price Changes',
  case_break:        'Case Breakdown',
  dealer_comm:       'Dealer Commission',
  time_rest:         'Time Restrictions',
  sft_file:          'SFT File',
  private_label:     'Private Label',
  imperial_import:   'Imperial Import',
  fuel_grade_report: 'Fuel Grade Report',
  inventory_control: 'Inventory Control'
};

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y || !m || !d) return '—';
  return `${MONTHS_SHORT[Number(m) - 1] || m} ${d}, ${y}`;
}

// Activity tone thresholds. Login + price book match the internal
// xsiteioTone helper (7/14 days). Fuel reconciliation runs on a
// MONTHLY cadence, so it gets its own scale (35/60) -- otherwise
// every site shows red on a perfectly normal month-end recon
// (Dave 2026-07-21, whole column was red).
function activityTone(value, goodDays = 7, warnDays = 14) {
  if (!value) return 'inactive';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return 'inactive';
  const days = (Date.now() - t) / 86400000;
  if (days <= goodDays) return 'good';
  if (days <= warnDays) return 'warn';
  return 'bad';
}

function ActivityCell({ value, goodDays, warnDays }) {
  const tone = activityTone(value, goodDays, warnDays);
  return (
    <td className="mono">
      <span className={`portal-dot portal-dot-${tone}`} aria-hidden="true" />
      {fmtDate(value)}
    </td>
  );
}

// Inline detail + edit panel rendered in an expanded row. Local form
// state so typing doesn't mutate the table until Save succeeds.
function SiteDetailPanel({ site, onSaved }) {
  const [phone, setPhone] = useState(site.site_phone || '');
  const [manager, setManager] = useState(site.site_manager || '');
  const [email, setEmail] = useState(site.site_email || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);
  const [showPw, setShowPw] = useState(false);

  const dirty =
    (phone || '') !== (site.site_phone || '') ||
    (manager || '') !== (site.site_manager || '') ||
    (email || '') !== (site.site_email || '');

  async function handleSave() {
    setErr(null);
    setSaving(true);
    try {
      await axios.put(`/api/portal/sites/${site.id}`, {
        site_phone: phone,
        site_manager: manager,
        site_email: email
      });
      onSaved({ ...site, site_phone: phone || null, site_manager: manager || null, site_email: email || null });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="portal-site-detail">
      <div className="portal-site-detail-col">
        <h4>Address</h4>
        <p>
          {site.address || '—'}<br />
          {[site.city, site.province_state, site.postal_zip].filter(Boolean).join(', ')}
        </p>
        <h4>System</h4>
        <p>{site.pos_type || '—'} · <span className="mono">{site.site_number}</span></p>
        <h4>XSite Login</h4>
        <p className="mono" style={{ marginBottom: 'var(--space-1)' }}>
          {site.xsite_io_username || '—'}
        </p>
        <p className="mono">
          {site.xsite_io_password ? (
            <>
              {showPw ? site.xsite_io_password : '••••••••'}
              <button
                type="button"
                className="portal-pw-toggle"
                onClick={() => setShowPw((v) => !v)}
              >
                {showPw ? 'Hide' : 'Show'}
              </button>
            </>
          ) : (
            <span style={{ color: 'var(--color-text-faint)' }}>
              Password on file with XSite — contact support to reset
            </span>
          )}
        </p>
      </div>
      <div className="portal-site-detail-col">
        <h4>Site Contact <span className="portal-editable-hint">(editable)</span></h4>
        <label>
          Phone
          <input className="form-input" value={phone} maxLength={30}
                 onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 403-555-0100" />
        </label>
        <label>
          Manager
          <input className="form-input" value={manager} maxLength={100}
                 onChange={(e) => setManager(e.target.value)} placeholder="Manager name" />
        </label>
        <label>
          Site email
          <input className="form-input" type="email" value={email} maxLength={100}
                 onChange={(e) => setEmail(e.target.value)} placeholder="site@example.com" />
        </label>
        {err && <p className="portal-error" style={{ padding: 'var(--space-2) var(--space-3)' }}>{err}</p>}
        <div className="portal-site-detail-actions">
          <button type="button" className="btn-primary" disabled={!dirty || saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {saved && <span className="portal-saved">✓ Saved</span>}
        </div>
      </div>
      <div className="portal-site-detail-col">
        <h4>Subscription Modules</h4>
        {(site.modules || []).length > 0 ? (
          <ul className="portal-modules">
            {site.modules.map((m) => {
              const badge = MODULE_BADGES[m.key];
              return (
                <li key={m.key}>
                  {badge && <Badge tone={badge.tone}>{badge.abbr}</Badge>}
                  <span className="portal-module-label">{MODULE_LABELS[m.key] || m.key}</span>
                  {m.renewal_date && (
                    <span className="mono portal-module-date">{fmtDate(m.renewal_date)}</span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p style={{ color: 'var(--color-text-faint)' }}>No modules on record</p>
        )}
      </div>
    </div>
  );
}

// Clickable column header. asc = oldest first (surfacing stale sites),
// desc = most recent first, third click returns to default order.
function SortableTh({ label, sortKey, sort, onToggle }) {
  const active = sort?.key === sortKey;
  return (
    <th
      className={`portal-th-sortable${active ? ' is-active' : ''}`}
      onClick={() => onToggle(sortKey)}
      title="Click to sort"
    >
      {label}
      <span className="portal-sort-arrow" aria-hidden="true">
        {active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
      </span>
    </th>
  );
}

// Row + optional expanded detail row. Fragment so both <tr>s sit as
// siblings inside <tbody>.
function SiteRowGroup({ site: s, expanded, onToggle, onSaved }) {
  return (
    <>
      <tr className={`portal-site-row${expanded ? ' is-expanded' : ''}`} onClick={onToggle}>
        <td>
          <span className={`portal-chevron${expanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
          <span style={{ display: 'inline-block', verticalAlign: 'top' }}>
            <span style={{ display: 'block' }}>{s.site_name}</span>
            <span className="mono" style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-small)' }}>
              {s.site_number}
            </span>
          </span>
        </td>
        <td>{[s.city, s.province_state].filter(Boolean).join(', ')}</td>
        <td>{s.pos_type || '—'}</td>
        <td>
          <span className={`portal-dot portal-dot-${s.status === 'Active' ? 'good' : 'inactive'}`} aria-hidden="true" />
          {s.status}
        </td>
        {/* Shift data arrives continuously from the POS -- tight 2/5
            day thresholds. A red here = Bulloch feed problem. */}
        <ActivityCell value={s.xsiteio_last_shift_at} goodDays={2} warnDays={5} />
        <ActivityCell value={s.xsiteio_last_login} />
        <ActivityCell value={s.xsiteio_last_updated} />
        <ActivityCell value={s.xsiteio_last_fuel_recon_at} goodDays={35} warnDays={60} />
        <ActivityCell value={s.xsiteio_last_po_created_at} />
      </tr>
      {expanded && (
        <tr className="portal-site-detail-row">
          {/* stopPropagation so clicking inputs doesn't collapse the row */}
          <td colSpan={9} onClick={(e) => e.stopPropagation()}>
            <SiteDetailPanel site={s} onSaved={onSaved} />
          </td>
        </tr>
      )}
    </>
  );
}

export default function PortalSites() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [province, setProvince] = useState('');
  // sort: null = default order (active first, then site number). Date
  // sorts put the OLDEST first on the first click -- "show me the sites
  // that haven't checked in" is the question being asked.
  const [sort, setSort] = useState(null); // { key, dir: 'asc'|'desc' }

  function toggleSort(key) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null; // third click clears back to default order
    });
  }

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/portal/sites')
      .then((res) => { if (!cancelled) setRows(res.data); })
      .catch(() => { if (!cancelled) setError('Failed to load your sites. Please try again.'); });
    return () => { cancelled = true; };
  }, []);

  const provinces = useMemo(() => {
    if (!rows) return [];
    return [...new Set(rows.map((s) => s.province_state).filter(Boolean))].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    const out = rows.filter((s) => {
      if (!showInactive && s.status !== 'Active') return false;
      if (province && s.province_state !== province) return false;
      if (!needle) return true;
      return [s.site_name, s.site_number, s.city, s.province_state]
        .some((v) => String(v || '').toLowerCase().includes(needle));
    });
    if (sort) {
      const { key, dir } = sort;
      out.sort((a, b) => {
        const ta = a[key] ? new Date(a[key]).getTime() : null;
        const tb = b[key] ? new Date(b[key]).getTime() : null;
        // Sites with no timestamp always sink to the bottom regardless
        // of direction -- "never" isn't oldest, it's unknown.
        if (ta === null && tb === null) return 0;
        if (ta === null) return 1;
        if (tb === null) return -1;
        return dir === 'asc' ? ta - tb : tb - ta;
      });
    }
    return out;
  }, [rows, q, showInactive, province, sort]);

  const activeCount = rows ? rows.filter((s) => s.status === 'Active').length : 0;

  return (
    <section className="page">
      <div className="page-header">
        <h2>Your Sites</h2>
        {rows && (
          <span className="portal-count">
            {activeCount} active{rows.length !== activeCount ? ` · ${rows.length - activeCount} inactive` : ''}
          </span>
        )}
      </div>

      {error && <p className="portal-error">{error}</p>}
      {!rows && !error && <p className="placeholder-note">Loading…</p>}

      {rows && (
        <>
          <div className="portal-toolbar">
            <input
              className="form-input"
              placeholder="Search site, city…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ maxWidth: 260 }}
            />
            <select
              className="form-input"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              style={{ maxWidth: 180 }}
              aria-label="Filter by province"
            >
              <option value="">All provinces</option>
              {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <label className="portal-checkbox">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show inactive sites
            </label>
          </div>

          <div className="table-wrap">
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Location</th>
                  <th>POS</th>
                  <th>Status</th>
                  <SortableTh label="Last Shift Data" sortKey="xsiteio_last_shift_at" sort={sort} onToggle={toggleSort} />
                  <SortableTh label="Last Login" sortKey="xsiteio_last_login" sort={sort} onToggle={toggleSort} />
                  <SortableTh label="Last Price Book Update" sortKey="xsiteio_last_updated" sort={sort} onToggle={toggleSort} />
                  <SortableTh label="Last Fuel Reconciliation" sortKey="xsiteio_last_fuel_recon_at" sort={sort} onToggle={toggleSort} />
                  <SortableTh label="Last Purchase Order" sortKey="xsiteio_last_po_created_at" sort={sort} onToggle={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <SiteRowGroup
                    key={s.id}
                    site={s}
                    expanded={expandedId === s.id}
                    onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
                    onSaved={(updated) => setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))}
                  />
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="placeholder-note">No sites match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="portal-footnote">
            Shift data flows continuously from your POS: green = within 2 days, amber = within 5, red = older
            (a red here usually means a data-feed issue — contact us).
            Login &amp; price book dots: green = within 7 days, amber = within 14, red = older.
            Fuel reconciliation runs monthly: green = within 35 days, amber = within 60, red = older.
            Click any site to see details and update its contact info.
            Questions about a site? Head to the <a href="/portal/support">Support</a> tab.
          </p>
        </>
      )}
    </section>
  );
}
